import { Dialog, Listbox, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { CheckCircleIcon } from "lucide-react";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { toast } from "@/shadcn/hooks/use-toast";
import { priorities, types } from "@/shadcn/lib/utils";
import { CheckIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import FileUploader from "../../components/FileUploader";
import { StarIcon } from "lucide-react";
import Link from "next/link";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

type errorType = {
    issue?: string;
    priority?: string;
    clientMember?: string;
    name?: string;
    email?: string;
    subject?: string;
    description?: string;
};

function ErrorList({ errors }: { errors: Record<string, string> }) {
    if (!errors || Object.keys(errors).length === 0) return null;
    return (
        <ul className="px-4 py-2 mb-2 text-xs text-left text-white bg-red-600 rounded">
            {Object.values(errors).map((msg, idx) => (
                <li key={idx}>{msg}</li>
            ))}
        </ul>
    );
}

export default function CreateTicketPortalModal({ clientId }) {
    const [open, setOpen] = useState(false);
    const handleKeyPress = (event: KeyboardEvent) => {
        // Check for Ctrl or Meta key to bypass the shortcut handler
        if (event.ctrlKey || event.metaKey) {
            return; // Don't override browser shortcuts
        }
        if (event.key === "Escape") {
            setOpen(false);
            // Clear the hash to remove the modal from the URL
            window.location.hash = "";
        }
    };

    const handleHashChange = () => {
        if (window.location.hash.startsWith("#!create-issue")) {
            setOpen(true);
        }
    };

    useEffect(() => {
        // attach the event listener
        document.addEventListener("keydown", handleKeyPress);
        // Listen for hashchange to open issue modal if hash is present

        window.addEventListener("hashchange", handleHashChange);

        // Check if the hash is already present on initial load
        if (window.location.hash.startsWith("#!create-issue")) {
            setOpen(true);
        }
        // remove the event listeners
        return () => {
            document.removeEventListener("keydown", handleKeyPress);
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, []);

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                onClose={() => {
                    setOpen(false);
                    // Clear the hash to remove the modal from the URL
                    window.location.hash = "";
                }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black bg-opacity-40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-full p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-2xl overflow-hidden text-left align-middle transition-all transform bg-gray-100 shadow-xl rounded-2xl">
                                <CreateTicketPortal clientId={clientId} />
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

export function CreateTicketPortal({ clientId, onError }: any) {
    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState("new");
    const [ticketID, setTicketID] = useState("");

    const [selected, setSelected] = useState(types[0]);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState(priorities[1]);

    const [client, setClient] = useState<any>(null);
    const [clientMember, setClientMember] = useState<any>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        if (clientId) {
            fetch(`/api/v1/client/${clientId}/public`, {
                method: "GET",
                headers: {
                    "content-type": "application/json",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.success) setClient(data.client);
                    else {
                        toast({
                            variant: "destructive",
                            title: "Error",
                            description: "Client not found or you do not have access to this client.",
                        });

                        setNotFound(true);
                    }
                });
        }
    }, [clientId]);

    const [errors, setErrors] = useState<errorType>({});

    async function submitTicket() {
        setIsLoading(true);

        const newErrors: errorType = {};

        if (!selected) newErrors.issue = "Please select an issue type.";
        if (!priority) newErrors.priority = "Please select a priority.";
        if (client && client.members && client.members.length > 0 && !clientMember) newErrors.clientMember = "Please select a client member.";
        if (clientMember == "new") {
            if (!name || name.trim().length < 2) newErrors.name = "Please enter your name.";
            if (!email || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(email)) newErrors.email = "Please enter a valid email address.";
        } else if (client && client.members && client.members.length > 0 && !clientMember) newErrors.clientMember = "Please select a client member.";
        
        if (!subject || subject.trim().length < 3) newErrors.subject = "Please enter a subject.";
        if (!description || description.trim().length < 5) newErrors.description = "Please enter a description.";
        if (client && client.members && client.members.length > 0 && !clientMember) {
            newErrors.clientMember = "Please select a client member.";
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            toast({
                variant: "destructive",
                title: "Error",
                description: (
                    <div>
                        Please fix the errors and try again:
                        <ErrorList errors={newErrors} />
                    </div>
                ),
            });
            setIsLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append("name", name);
        formData.append("title", subject);
        formData.append("company", clientId);
        formData.append("email", email);
        formData.append("detail", description);
        formData.append("priority", priority.id);
        formData.append("type", selected.name);
        if (client) formData.append("client", client.id);
        if (clientMember) formData.append("clientMember", clientMember.id);
        files.forEach((file) => {
            formData.append("files", file);
        });

        await fetch(`/api/v1/ticket/create/public`, {
            method: "POST",
            body: formData,
        })
            .then((res) => res.json())
            .then((res) => {
                setIsLoading(false);
                if (res.success === true) {
                    toast({
                        variant: "default",
                        title: "Success",
                        description: "Ticket created successfully",
                    });
                    setView("success");
                    setTicketID(res.id);
                } else {
                    toast({
                        variant: "destructive",
                        title: "Error",
                        description: `Please fill out all information and try again`,
                    });
                }
            })
            .catch((err) => {
                setIsLoading(false);
                console.error("Error creating ticket:", err);
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "An error occurred while creating the ticket. Please try again later.",
                });
            });
    }

    if (!client) {
        if (notFound) {
            return (
                <div className="flex items-center justify-center min-h-screen bg-gray-900">
                    <div className="flex flex-col items-center p-8 bg-white rounded shadow">
                        <span className="mb-2 text-4xl text-red-500">
                            <CheckCircleIcon className="w-12 h-12" aria-hidden="true" />
                        </span>
                        <h2 className="mb-2 text-xl font-semibold text-gray-900">Client Not Found</h2>
                        <p className="mb-4 mb-10 text-sm text-center text-gray-700">The client you are trying to access does not exist or you do not have access.</p>
                        <button
                            className="px-3 py-1.5 text-sm text-purple-700 transition-colors bg-transparent border border-purple-600 rounded-md shadow-sm hover:bg-purple-50 hover:text-purple-900"
                            onClick={() => onError()}>
                            Go to Portal
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-900">
                <ArrowPathIcon className="w-12 h-12 text-green-600 animate-spin" />
                <span className="ml-4 text-sm text-gray-200">Loading...</span>
            </div>
        );
    }

    return (
        <>
            {view === "new" ? (
                <div className="">
                    <div className="max-w-2xl p-8 bg-gray-100 rounded">
                        <div className="pb-4 mb-4 border-b">
                            <div className="flex items-center gap-4">
                                <h1 className="flex items-center flex-1 gap-1 text-xl">
                                    <StarIcon className="w-4 h-4" aria-hidden="true" /> {client.name}
                                </h1>
                                {client.members && client.members.length > 0 && (
                                    <div className="flex-1">
                                        <Listbox value={clientMember} onChange={setClientMember}>
                                            {({ open }) => (
                                                <>
                                                    <Listbox.Label className="block text-sm font-medium text-gray-900"></Listbox.Label>
                                                    <div className="relative">
                                                        <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                                                            <span className="block truncate">
                                                                {clientMember ? (clientMember == "new" ? "New Member" : clientMember.name) : "Select a team member"}
                                                            </span>
                                                            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                                <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                            </span>
                                                        </Listbox.Button>
                                                        <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                                            <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                {client.members.map((member: any) => (
                                                                    <Listbox.Option
                                                                        key={member.id}
                                                                        className={({ active }) =>
                                                                            classNames(
                                                                                active ? "bg-gray-400 text-white" : "text-gray-900",
                                                                                "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                            )
                                                                        }
                                                                        value={member}>
                                                                        {({ selected, active }) => (
                                                                            <>
                                                                                <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                                                                                    {member.name}
                                                                                </span>
                                                                                {selected ? (
                                                                                    <span
                                                                                        className={classNames(
                                                                                            active ? "text-white" : "text-indigo-600",
                                                                                            "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                        )}>
                                                                                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                    </span>
                                                                                ) : null}
                                                                            </>
                                                                        )}
                                                                    </Listbox.Option>
                                                                ))}
                                                                <div className="my-1 border-t border-gray-200" />
                                                                <Listbox.Option
                                                                    className={({ active }) =>
                                                                        classNames(
                                                                            active ? "bg-indigo-600 text-white" : "text-gray-900 dark:text-white",
                                                                            "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                        )
                                                                    }
                                                                    value={"new"}>
                                                                    {({ selected, active }) => (
                                                                        <>
                                                                            <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                                                                                New Member
                                                                            </span>

                                                                            {selected ? (
                                                                                <span
                                                                                    className={classNames(
                                                                                        active ? "text-white" : "text-indigo-600",
                                                                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                    )}>
                                                                                    <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                </span>
                                                                            ) : null}
                                                                        </>
                                                                    )}
                                                                </Listbox.Option>
                                                            </Listbox.Options>
                                                        </Transition>
                                                    </div>
                                                </>
                                            )}
                                        </Listbox>
                                        {errors.clientMember && <p className="mt-1 text-xs text-red-600">{errors.clientMember}</p>}
                                    </div>
                                )}
                            </div>
                            <div className={"flex flex-col gap-1 mt-4 p-4 bg-gray-200 border border-gray-300 rounded" + (clientMember == "new" ? "" : " hidden")}>
                                <div className="mb-3 text-sm opacity-40">Please enter your details below to add you as a member.</div>
                                <div className={"flex flex-row gap-4"}>
                                    <div className="flex-1">
                                        <label htmlFor="email" className="flex-1 block text-sm font-medium text-gray-900">
                                            Name
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                type="email"
                                                name="email"
                                                id="email"
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                                placeholder="John Doe"
                                                onChange={(e) => setName(e.target.value)}
                                                value={name}
                                                autoComplete="off"
                                            />
                                        </div>
                                        {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="email" className="flex-1 block text-sm font-medium text-gray-900">
                                            Email
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                type="email"
                                                name="email"
                                                id="email"
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                                placeholder="johnD@meta.com"
                                                onChange={(e) => setEmail(e.target.value)}
                                                value={email}
                                                autoComplete="off"
                                            />
                                        </div>
                                        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold">Submit a Ticket</h1>
                        <span className="text-sm">Submit a ticket and our support team will get back to you as soon as possible.</span>

                        <div className="flex flex-col gap-4 my-4">
                            <div className="flex flex-row gap-4">
                                <div className="flex-1">
                                    <Listbox value={selected} onChange={setSelected}>
                                        {({ open }) => (
                                            <>
                                                <Listbox.Label className="block text-sm font-medium text-gray-900">Issue Type</Listbox.Label>
                                                <div className="relative mt-1">
                                                    <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                                                        <span className="block truncate">{selected.name}</span>
                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                        </span>
                                                    </Listbox.Button>

                                                    <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                                        <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                            {types.map((person) => (
                                                                <Listbox.Option
                                                                    key={person.id}
                                                                    className={({ active }) =>
                                                                        classNames(
                                                                            active ? "bg-gray-400 text-white" : "text-gray-900",
                                                                            "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                        )
                                                                    }
                                                                    value={person}>
                                                                    {({ selected, active }) => (
                                                                        <>
                                                                            <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                                                                                {person.name}
                                                                            </span>

                                                                            {selected ? (
                                                                                <span
                                                                                    className={classNames(
                                                                                        active ? "text-white" : "text-indigo-600",
                                                                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                    )}>
                                                                                    <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                </span>
                                                                            ) : null}
                                                                        </>
                                                                    )}
                                                                </Listbox.Option>
                                                            ))}
                                                        </Listbox.Options>
                                                    </Transition>
                                                </div>
                                            </>
                                        )}
                                    </Listbox>
                                    {errors.issue && <p className="mt-1 text-xs text-red-600">{errors.issue}</p>}
                                </div>
                                <div className="flex-1">
                                    <Listbox value={priority} onChange={setPriority}>
                                        {({ open }) => (
                                            <>
                                                <Listbox.Label className="block text-sm font-medium text-gray-900">Priority</Listbox.Label>
                                                <div className="relative mt-1">
                                                    <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                                                        <span className="block truncate">{priority.name}</span>
                                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
                                                        </span>
                                                    </Listbox.Button>

                                                    <Transition show={open} as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                                        <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                            {priorities.map((priority) => (
                                                                <Listbox.Option
                                                                    key={priority.id}
                                                                    className={({ active }) =>
                                                                        classNames(
                                                                            active ? "bg-gray-400 text-white" : "text-gray-900",
                                                                            "relative cursor-default select-none py-2 pl-3 pr-9"
                                                                        )
                                                                    }
                                                                    value={priority}>
                                                                    {({ selected, active }) => (
                                                                        <>
                                                                            <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                                                                                {priority.name}
                                                                            </span>

                                                                            {selected ? (
                                                                                <span
                                                                                    className={classNames(
                                                                                        active ? "text-white" : "text-indigo-600",
                                                                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                                                                    )}>
                                                                                    <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                                                </span>
                                                                            ) : null}
                                                                        </>
                                                                    )}
                                                                </Listbox.Option>
                                                            ))}
                                                        </Listbox.Options>
                                                    </Transition>
                                                </div>
                                            </>
                                        )}
                                    </Listbox>
                                    {errors.priority && <p className="mt-1 text-xs text-red-600">{errors.priority}</p>}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                                    Subject
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                        placeholder="Experiencing slow performance on dashboard, bugs etc..."
                                        onChange={(e) => setSubject(e.target.value)}
                                        value={subject}
                                    />
                                </div>
                                {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
                            </div>
                            <div>
                                <label htmlFor="comment" className="block text-sm font-medium text-gray-900">
                                    Description of Issue
                                </label>
                                <div className="mt-1">
                                    <textarea
                                        rows={4}
                                        name="comment"
                                        id="comment"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        placeholder="Please explain what happened and what you expected to occur and describe the steps you took before the issue appeared..."
                                        onChange={(e) => setDescription(e.target.value)}
                                        value={description}
                                    />
                                </div>
                                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
                            </div>

                            <FileUploader files={files} onFileSelect={setFiles} />
                            <button
                                type="button"
                                onClick={submitTicket}
                                disabled={isLoading}
                                className="px-3 py-2 text-sm font-semibold text-white bg-purple-600 rounded-md shadow-sm hover:bg-purple-500 ">
                                {isLoading ? (
                                    <span className="flex items-center justify-center">
                                        <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                                        Submitting...
                                    </span>
                                ) : (
                                    "Submit Ticket"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <div className="p-8 bg-green-600 rounded-md shadow-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="w-10 h-10 text-white" aria-hidden="true" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-2xl font-medium text-white">Ticket Submitted</h3>
                                <div className="mt-1 text-sm text-white">
                                    <p>A member of our team has been notified and will be in touch shortly.</p>
                                </div>
                                <div className="mt-8">
                                    <Link onClick={() => {
                                        window.location.reload();
                                    }} href={`/${clientId}/ticket`} className="px-3 py-1.5 text-sm text-white transition-colors bg-transparent border rounded-md shadow-sm border-white-600 hover:bg-white hover:text-gray-900">
                                        Submit another ticket
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
